import { useState } from "react";
import { OrgPicker } from "@/components/OrgPicker";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
	Building2,
	Plus,
	Minus,
	CheckCircle2,
} from "lucide-react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/toaster";
import * as api from "@/services/api";

export function OrgBootstrapper() {
	const [selectedOrgId, setSelectedOrgId] = useState<string>("");
	const [customPackageEnabled, setCustomPackageEnabled] = useState(false);
	const [customFeatures, setCustomFeatures] = useState<string[]>([]);
	const [blockedFeatures, setBlockedFeatures] = useState<string[]>([]);

	const { data: orgs } = useQuery({
		queryKey: ["organizations"],
		queryFn: api.getOrganizations,
	});

	const { data: features } = useQuery({
		queryKey: ["features"],
		queryFn: api.getFeatures,
	});

	const { data: plans } = useQuery({
		queryKey: ["plans"],
		queryFn: api.getPlans,
	});

	const { refetch: refetchEffective } = useQuery({
		queryKey: ["effective-features", selectedOrgId],
		queryFn: () => api.getEffectiveFeatures(selectedOrgId),
		enabled: !!selectedOrgId,
	});

	const selectedOrg = orgs?.find((o) => o._id === selectedOrgId);
	const selectedPlan = plans?.find((p) => p.slug === selectedOrg?.plan);

	const updateCustomPackageMutation = useMutation({
		mutationFn: (data: {
			enabled?: boolean;
			customFeatures?: string[];
			blockedFeatures?: string[];
		}) => api.updateCustomPackage(selectedOrgId, data),
		onSuccess: () => {
			refetchEffective();
			toast({ title: "Custom package updated", variant: "success" });
		},
		onError: (error) => {
			toast({
				title: "Failed to update",
				description: String(error),
				variant: "destructive",
			});
		},
	});

	const handleOrgSelect = (orgId: string) => {
		setSelectedOrgId(orgId);
		const org = orgs?.find((o) => o._id === orgId);
		if (org?.customPackage) {
			setCustomPackageEnabled(org.customPackage.enabled);
			setCustomFeatures(org.customPackage.customFeatures || []);
			setBlockedFeatures(org.customPackage.blockedFeatures || []);
		} else {
			setCustomPackageEnabled(false);
			setCustomFeatures([]);
			setBlockedFeatures([]);
		}
	};

	const toggleCustomFeature = (featureSlug: string) => {
		if (customFeatures.includes(featureSlug)) {
			setCustomFeatures(customFeatures.filter((f) => f !== featureSlug));
		} else {
			setCustomFeatures([...customFeatures, featureSlug]);
			// Remove from blocked if it was there
			setBlockedFeatures(blockedFeatures.filter((f) => f !== featureSlug));
		}
	};

	const toggleBlockedFeature = (featureSlug: string) => {
		if (blockedFeatures.includes(featureSlug)) {
			setBlockedFeatures(blockedFeatures.filter((f) => f !== featureSlug));
		} else {
			setBlockedFeatures([...blockedFeatures, featureSlug]);
			// Remove from custom if it was there
			setCustomFeatures(customFeatures.filter((f) => f !== featureSlug));
		}
	};

	const saveCustomPackage = () => {
		updateCustomPackageMutation.mutate({
			enabled: customPackageEnabled,
			customFeatures,
			blockedFeatures,
		});
	};

	// Calculate preview - normalize features to strings (backend may return objects or strings)
	const basePlanFeatures = (selectedPlan?.features || []).map((f: any) =>
		typeof f === "string" ? f : f.slug || f.code,
	);
	const previewEffective = [
		...basePlanFeatures.filter((f) => !blockedFeatures.includes(f)),
		...customFeatures.filter((f) => !basePlanFeatures.includes(f)),
	];

	// Get features not in base plan (for adding)
	const availableToAdd =
		features?.filter(
			(f) =>
				!basePlanFeatures.includes(f.slug) && !customFeatures.includes(f.slug),
		) || [];

	// Get features in base plan (for blocking)
	const availableToBlock =
		features?.filter(
			(f) =>
				basePlanFeatures.includes(f.slug) && !blockedFeatures.includes(f.slug),
		) || [];

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold text-gray-900">Org Bootstrapper</h1>
				<p className="text-gray-600 mt-2">
					Configure custom feature packages for organizations
				</p>
			</div>

			{/* Organization Selector */}
			<Card>
				<CardHeader>
					<CardTitle>Select Organization</CardTitle>
					<CardDescription>
						Choose an organization to configure
					</CardDescription>
				</CardHeader>
				<CardContent>
					<OrgPicker
						orgs={orgs}
						value={selectedOrgId}
						onChange={handleOrgSelect}
						className="max-w-md"
					/>
				</CardContent>
			</Card>

			{selectedOrg && (
				<>
					{/* Organization Info */}
					<Card>
						<CardHeader>
							<div className="flex items-center justify-between">
								<div>
									<CardTitle className="flex items-center gap-2">
										<Building2 className="w-5 h-5" />
										{selectedOrg.name}
									</CardTitle>
									<CardDescription>
										Base Plan: {selectedPlan?.name || selectedOrg.plan}
									</CardDescription>
								</div>
								<Badge
									variant={
										selectedOrg.subscriptionStatus === "active"
											? "success"
											: "secondary"
									}
								>
									{selectedOrg.subscriptionStatus || "unknown"}
								</Badge>
							</div>
						</CardHeader>
						<CardContent>
							<div className="grid grid-cols-3 gap-4">
								<div>
									<p className="text-sm text-muted-foreground">Base Features</p>
									<p className="text-2xl font-bold">
										{basePlanFeatures.length}
									</p>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">
										Custom Additions
									</p>
									<p className="text-2xl font-bold text-green-600">
										+{customFeatures.length}
									</p>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">Blocked</p>
									<p className="text-2xl font-bold text-red-600">
										-{blockedFeatures.length}
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Custom Package Toggle */}
					<Card>
						<CardHeader>
							<div className="flex items-center justify-between">
								<div>
									<CardTitle>Custom Package</CardTitle>
									<CardDescription>
										Enable to customize features beyond the base plan
									</CardDescription>
								</div>
								<Switch
									checked={customPackageEnabled}
									onCheckedChange={setCustomPackageEnabled}
								/>
							</div>
						</CardHeader>
					</Card>

					{customPackageEnabled && (
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
							{/* Add Custom Features */}
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-green-700">
										<Plus className="w-5 h-5" />
										Add Custom Features
									</CardTitle>
									<CardDescription>
										Features to add beyond the base plan
									</CardDescription>
								</CardHeader>
								<CardContent>
									{/* Currently Added */}
									{customFeatures.length > 0 && (
										<div className="mb-4">
											<p className="text-sm font-medium mb-2">Added:</p>
											<div className="flex flex-wrap gap-2">
												{customFeatures.map((f) => (
													<Badge
														key={f}
														variant="outline"
														className="cursor-pointer hover:bg-red-50 border-green-500 text-green-700"
														onClick={() => toggleCustomFeature(f)}
													>
														{f}
														<Minus className="w-3 h-3 ml-1" />
													</Badge>
												))}
											</div>
										</div>
									)}

									{/* Available to Add */}
									<p className="text-sm font-medium mb-2">
										Available to add:
									</p>
									<div className="max-h-48 overflow-y-auto space-y-1">
										{availableToAdd.map((feature) => (
											<div
												key={feature.slug}
												className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
												onClick={() => toggleCustomFeature(feature.slug)}
											>
												<div>
													<span className="text-sm">
														{feature.ui?.label || feature.slug}
													</span>
													<span className="text-xs text-muted-foreground ml-2">
														({feature.module})
													</span>
												</div>
												<Plus className="w-4 h-4 text-green-600" />
											</div>
										))}
									</div>
								</CardContent>
							</Card>

							{/* Block Features */}
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2 text-red-700">
										<Minus className="w-5 h-5" />
										Block Features
									</CardTitle>
									<CardDescription>
										Features to remove from the base plan
									</CardDescription>
								</CardHeader>
								<CardContent>
									{/* Currently Blocked */}
									{blockedFeatures.length > 0 && (
										<div className="mb-4">
											<p className="text-sm font-medium mb-2">Blocked:</p>
											<div className="flex flex-wrap gap-2">
												{blockedFeatures.map((f) => (
													<Badge
														key={f}
														variant="outline"
														className="cursor-pointer hover:bg-green-50 border-red-500 text-red-700"
														onClick={() => toggleBlockedFeature(f)}
													>
														{f}
														<Plus className="w-3 h-3 ml-1" />
													</Badge>
												))}
											</div>
										</div>
									)}

									{/* Available to Block */}
									<p className="text-sm font-medium mb-2">
										Available to block:
									</p>
									<div className="max-h-48 overflow-y-auto space-y-1">
										{availableToBlock.map((feature) => (
											<div
												key={feature.slug}
												className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
												onClick={() => toggleBlockedFeature(feature.slug)}
											>
												<div>
													<span className="text-sm">
														{feature.ui?.label || feature.slug}
													</span>
													<span className="text-xs text-muted-foreground ml-2">
														({feature.module})
													</span>
												</div>
												<Minus className="w-4 h-4 text-red-600" />
											</div>
										))}
									</div>
								</CardContent>
							</Card>
						</div>
					)}

					{/* Preview & Save */}
					<Card>
						<CardHeader>
							<CardTitle>Preview Effective Features</CardTitle>
							<CardDescription>
								These are the features this organization will have access to
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="mb-4 p-4 bg-gray-50 rounded-lg">
								<div className="text-2xl font-bold mb-2">
									{previewEffective.length} features
								</div>
								<div className="flex flex-wrap gap-1">
									{previewEffective.slice(0, 20).map((f) => (
										<Badge key={f} variant="secondary" className="text-xs">
											{f}
										</Badge>
									))}
									{previewEffective.length > 20 && (
										<Badge variant="outline" className="text-xs">
											+{previewEffective.length - 20} more
										</Badge>
									)}
								</div>
							</div>

							<Button
								onClick={saveCustomPackage}
								disabled={updateCustomPackageMutation.isPending}
								className="w-full"
							>
								{updateCustomPackageMutation.isPending ? (
									"Saving..."
								) : (
									<>
										<CheckCircle2 className="w-4 h-4 mr-2" />
										Save Custom Package
									</>
								)}
							</Button>
						</CardContent>
					</Card>
				</>
			)}

			{!selectedOrg && (
				<Card className="border-dashed">
					<CardContent className="py-12 text-center">
						<Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
						<h3 className="font-medium text-gray-600">No organization selected</h3>
						<p className="text-sm text-muted-foreground mt-1">
							Select an organization above to configure its custom package
						</p>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
